import {useEffect, useState} from 'react';
import {FormState} from './ValidatedForm';
import {InvalidStatus, ValidationFieldName, ValidationResult} from "../component/ValidationComponent";

type UseFormValidationProps = {
    submitForm: () => void;
}

export const useFormValidation = <T>({submitForm}: UseFormValidationProps) => {

    type ErrorSearchResult = { idx: number, value: InvalidStatus<T>, exists: true } | { exists: false };

    const [errorState, setErrorsState] = useState<InvalidStatus<T>[]>([]);
    const [formState, setFormState] = useState<FormState>({formDisabled: false, formDirty: false});
    const {formDisabled, formDirty} = formState;

    useEffect(() => {
        return () => {
            setFormState({formDirty: false, formDisabled: false})
            setErrorsState([]);
        }
    }, [])

    useEffect(() => {
        setFormState(prevState => {
            const update = {
                formDirty: prevState.formDirty,
                formDisabled: (errorState.length !== 0 && prevState.formDirty)
            };
            if (JSON.stringify(update) !== JSON.stringify(formState)) {
                return update
            } else {
                return prevState;
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorState, formDirty]);

    useEffect(() => {
        if (formDirty) {
            setErrorsState(prevState => prevState.map(v => ({...v, dirty: true})))
        }
    }, [formDirty])

    const replaceErrorAtIndex = (idx: number,
                                 status: InvalidStatus<T>,
                                 state: InvalidStatus<T>[]) => {
        const errorCopy = state.slice();
        errorCopy.splice(idx, 1, status);
        return errorCopy
    };

    const addErrorToState = (status: InvalidStatus<T>,
                             state: InvalidStatus<T>[]) => {
        const errorStateCopy = state.slice();
        errorStateCopy.push(status)
        return errorStateCopy
    };

    const updateValidationStatus = (status: ValidationResult<T>) => {
        if (!status.valid) {
            createOrUpdateError(status);
        } else {
            removeErrorByFieldName(status.fieldName);
        }
    }

    const createOrUpdateError = (status: InvalidStatus<T>) => {
        setErrorsState((prevState) => {
            const searchRes = findErrorByFieldName(prevState, status.fieldName);
            status.dirty = status.dirty || formDirty
            if (searchRes.exists) {
                return replaceErrorAtIndex(searchRes.idx, status, prevState);
            } else {
                return addErrorToState(status, prevState);
            }
        });
    };

    const removeErrorByFieldName = (fieldName: ValidationFieldName<T>) => {
        setErrorsState(prevState => {
            const searchRes = findErrorByFieldName(prevState, fieldName);
            if (searchRes.exists) {
                const errorStateCopy = prevState.slice();
                errorStateCopy.splice(searchRes.idx, 1);
                return errorStateCopy
            }
            return prevState
        });
    };

    const getFormValid = () => {
        return errorState.length === 0;
    }

    const getMessage = (fieldName: ValidationFieldName<T>) => {
        if (formDirty) {
            const searchRes = findErrorByFieldName(errorState, fieldName);
            if (searchRes.exists) {
                if (searchRes.value.dirty) {
                    return searchRes.value.message
                }
            }
        }
        return undefined;
    }

    const onSubmitClick = () => {
        if (getFormValid()) {
            submitForm();
        } else {
            setFormState({formDirty: true, formDisabled: true})
        }
    }

    function findErrorByFieldName(errorState: InvalidStatus<T>[], fieldName: ValidationFieldName<T>): ErrorSearchResult {
        const idx = errorState.findIndex(obj => obj.fieldName === fieldName);
        const exists = idx !== -1;
        if (exists) {
            const value = errorState[idx];
            return {idx, value, exists};
        } else {
            return {exists};
        }
    }

    const errorMessages: string[] = errorState
        .filter(value => !value.valid)
        .filter(s => s.dirty && formDirty)
        .map(s => s.message as string);

    return {
        errorMessages,
        updateValidationStatus,
        getMessage,
        removeErrorByFieldName,
        formDirty,
        formDisabled,
        setFormState,
        onSubmitClick,
        formState
    }
}
